const days = [
  { day: 1, title: 'Arrival & Ubud Welcome', items: ['Airport Transfer (Private)', 'Ubud Palace & Markets', 'Traditional Balinese Dinner'] },
  { day: 2, title: 'Temples & Rice Terraces', items: ['Tegallalang Rice Terrace', 'Tirta Empul Temple', 'Cooking Class'] },
  { day: 3, title: 'Seminyak Beach Day', items: ['Beach Club Access', 'Sunset Dinner — Ku De Ta', 'Spa & Wellness (60 min)'] },
];

export default function ItineraryBuilderModule() {
  return (
    <section id="itinerary-builder" className="py-24 px-6 bg-gray-50">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Left: Itinerary mockup */}
          <div className="order-2 lg:order-1">
            <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
              {/* Header */}
              <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between bg-gradient-to-r from-teal-500 to-emerald-500">
                <div>
                  <p className="text-white font-semibold text-sm">Bali Romantic Escape</p>
                  <p className="text-teal-100 text-xs">7 Days • 2 Pax • Apr 12–19</p>
                </div>
                <div className="flex gap-1">
                  <button type="button" className="text-[10px] bg-white/20 hover:bg-white/30 text-white px-2 py-1 rounded cursor-pointer whitespace-nowrap transition-colors">
                    <i className="ri-magic-line mr-1" />AI Fill
                  </button>
                  <button type="button" className="text-[10px] bg-white text-teal-700 px-2 py-1 rounded cursor-pointer whitespace-nowrap hover:bg-teal-50 transition-colors">
                    + Add Day
                  </button>
                </div>
              </div>

              {/* Day cards */}
              <div className="p-4 space-y-3">
                {days.map((d) => (
                  <div key={d.day} className="border border-gray-100 rounded-xl overflow-hidden">
                    <div className="bg-gray-50 px-4 py-2 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="w-6 h-6 bg-gradient-to-br from-teal-500 to-emerald-500 rounded-full flex items-center justify-center text-white text-[10px] font-bold">
                          {d.day}
                        </span>
                        <p className="text-xs font-semibold text-gray-800">{d.title}</p>
                      </div>
                      <button type="button" className="text-[10px] text-teal-600 hover:text-teal-800 cursor-pointer">
                        <i className="ri-add-line" /> Add
                      </button>
                    </div>
                    <div className="px-4 py-3 space-y-1.5">
                      {d.items.map((item) => (
                        <div key={item} className="flex items-center gap-2">
                          <i className="ri-checkbox-circle-fill text-teal-400 text-xs" />
                          <p className="text-xs text-gray-600">{item}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}

                {/* Total */}
                <div className="bg-teal-50 rounded-xl px-4 py-3 flex items-center justify-between">
                  <div>
                    <p className="text-xs font-semibold text-teal-800">Package Total</p>
                    <p className="text-[10px] text-teal-600">7 days • Flights, Hotel, Transfers, Activities</p>
                  </div>
                  <p className="text-lg font-bold text-teal-700">$2,750</p>
                </div>

                <button type="button" className="w-full bg-gradient-to-r from-teal-500 to-emerald-500 text-white text-xs font-semibold py-2.5 rounded-xl hover:from-teal-600 hover:to-emerald-600 transition-all cursor-pointer whitespace-nowrap">
                  <i className="ri-file-pdf-line mr-1.5" />Convert to Quote PDF
                </button>
              </div>
            </div>
          </div>

          {/* Right: Content */}
          <div className="order-1 lg:order-2">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 flex items-center justify-center bg-teal-50 rounded-lg">
                <i className="ri-map-2-line text-teal-500" />
              </div>
              <span className="text-xs font-semibold uppercase tracking-widest text-teal-600">Module 04</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-semibold text-gray-900 leading-tight tracking-tight mb-4">
              AI Itinerary Builder —<br />
              <span className="bg-gradient-to-r from-teal-500 to-emerald-500 bg-clip-text text-transparent">Days Built in Minutes</span>
            </h2>
            <p className="text-gray-500 text-base leading-relaxed mb-6">
              Build a full day-by-day travel itinerary in minutes, not hours. Start from scratch, let AI fill the days based on destination and preferences, or use your own pre-saved templates. Every itinerary feeds directly into the quote builder.
            </p>
            <ul className="flex flex-col gap-4">
              {[
                { icon: 'ri-magic-line', title: 'AI-powered day suggestions', desc: 'Enter destination, duration, and travel style. AI fills in activities, hotels, and transfers as a starting point.' },
                { icon: 'ri-user-settings-line', title: 'Fully customisable by agent', desc: 'Drag, add, or remove services from each day. Your own prefilled templates can be stored and reused per trip type.' },
                { icon: 'ri-price-tag-3-line', title: 'Live pricing summary', desc: 'As you build the itinerary, a running total calculates instantly. Margins and costs are tracked per item.' },
                { icon: 'ri-arrow-right-circle-line', title: 'One-click to quote', desc: 'When the itinerary is ready, click Convert to Quote — it becomes a fully branded PDF ready to send.' },
              ].map((item) => (
                <li key={item.title} className="flex items-start gap-3">
                  <div className="w-8 h-8 flex items-center justify-center bg-teal-50 rounded-lg shrink-0 mt-0.5">
                    <i className={`${item.icon} text-teal-600 text-sm`} />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-800 mb-0.5">{item.title}</p>
                    <p className="text-xs text-gray-500 leading-relaxed">{item.desc}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}
